import { Assertion, AssertionType, ItemType, NewAssertion, PersonDisplayProperties } from "./ResearchDataModel"
import { usePersonStore } from './PersonStore'
import { useFamilyStore } from './FamilyStore'
import { useResearchDataApi } from './ResearchDataApi'
import { AsyncData, LoadMode } from "@webapp/util/AsyncData"
import { AsyncDataStore } from "@webapp/util/AsyncDataStore"
import { defineStore } from "pinia"
import { DateTime } from "luxon"
import { DateTimeUtil } from "@webapp/util/LuxonUtil"
import _, { uniqBy } from "lodash"
import { assignExisting, isDefined } from "@webapp/util/TypeScriptUtil"
import { HistoricalDate } from './HistoricalDate'
import { SortDate } from './SortDate'
import { PatchChange } from '@webapp/util/Api'
import { CompositeId } from './CompositeId'
import { usePlaceStore } from './PlaceStore'
import { useSiteStore } from './SiteStore'
import { useViewPersonStore } from './ViewPersonStore'
import { useViewFamilyStore } from './ViewFamilyStore'


export const useAssertionStore = defineStore("assertionStore", () => {
    const asyncAssertions = new Map<string, AsyncData<Assertion>>()

    const rd = useResearchDataApi()
    const personStore = usePersonStore()
    const familyStore = useFamilyStore()
    const placeStore = usePlaceStore()
    const siteStore = useSiteStore()
    const viewPersonStore = useViewPersonStore()
    const viewFamilyStore = useViewFamilyStore()

    function getAsyncAssertion(assertionId: string | undefined, loadMode = LoadMode.TrackOnly) {
        return assertionId ? getAsyncAssertionList([assertionId], loadMode)[0] : undefined
    }

    function getAsyncAssertionList(assertionIds: string[], loadMode = LoadMode.TrackOnly) {
        if (loadMode != LoadMode.TrackOnly) {
            throw `AssertionStore: Load mode not implemented`
        }
        const store = useAssertionStore() // use to get/return reactive store objects
        AsyncDataStore.getAsyncItemsToLoad(store.asyncAssertions, assertionIds, "Assertion", loadMode)
        return assertionIds.map(id => store.asyncAssertions.get(id)!)
    }

    function areLoadedForPerson(personId: string) {
        const keyLists = AsyncDataStore.getAsyncItemsIfAllLoaded(personStore.asyncAssertionKeys, [personId])
        if (keyLists) {
            const store = useAssertionStore() // use to get/return reactive store objects
            const assertionIds = keyLists[0].data?.keys ?? []
            return AsyncDataStore.areItemsLoaded(store.asyncAssertions, assertionIds)
        }
        return false
    }

    function getLoadedForPerson(personId: string) {
        const keyLists = AsyncDataStore.getAsyncItemsIfAllLoaded(personStore.asyncAssertionKeys, [personId])
        const assertionIds = keyLists?.[0].data?.keys ?? []
        return getAsyncAssertionList(assertionIds).map(a => a.data).filter(isDefined)
    }

    function getLoadedForFamily(familyId: string | undefined) {
        const assertionIds = familyStore.asyncAssertionKeys.get(familyId ?? '')?.data?.keys ?? []
        return getAsyncAssertionList(assertionIds).map(a => a.data).filter(isDefined)
    }

    function areExpandedAssertionsLoaded(assertionIds: string[]) {
        // check assertions loaded
        const store = useAssertionStore() // use to get/return reactive store objects
        const assertions = AsyncDataStore.getAsyncItemsIfAllLoaded(store.asyncAssertions, assertionIds)
        if (!assertions)
            return false

        // check places loaded
        const placeIds = [...new Set(assertions.map(a => a.data?.placeId).filter(isDefined))]
        if (!AsyncDataStore.areItemsLoaded(placeStore.asyncPlaces, placeIds))
            return false

        // check sites loaded
        const siteIds = [...new Set(assertions.map(a => a.data?.siteId).filter(isDefined))]
        if (!AsyncDataStore.areItemsLoaded(siteStore.asyncSites, siteIds))
            return false

        return true
    }

    async function addAsync(groupId: string, assertion: NewAssertion) {
        if (CompositeId.getGroupId(assertion.subjectId) != groupId)
            throw `Assertion subject ${assertion.subjectId} does not belong to group ${groupId}`
        if (assertion.subjectType != ItemType.Person &&
            assertion.subjectType != ItemType.Family)
            throw `Assertion subject added to unexpected type: ${assertion.subjectType}`

        const addCoreAsync = async () => {
            const protocolItem = {
                ...assertion,
                date: assertion.date?.toString(),
                sortDate: assertion.sortDate?.toString(),
            }
            const p = await rd.postPlainAsync(`groups/${groupId}/assertions`, protocolItem)
            return plainToAssertion(p)
        }            
        const store = useAssertionStore() // use to get/return reactive store objects
        const newKey = await AsyncDataStore.addToStoreMapAsync(store.asyncAssertions, "Assertion", a => a.id, addCoreAsync)

        const asyncAssertion = store.asyncAssertions.get(newKey)!
        invalidateRelated(asyncAssertion)

        return newKey
    }

    async function patchAsync(id: string, changes: PatchChange[]) {
        const patchCoreAsync = async (id: string, changes: PatchChange[]) => {
            const p = await rd.patchPlainAsync(`assertions/${id}`, changes);
            return plainToAssertion(p);
        }
        const store = useAssertionStore() // use to get/return reactive store objects
        await AsyncDataStore.patchFromStoreAsync(store.asyncAssertions, id, "Assertion", changes, patchCoreAsync)

        const a = store.asyncAssertions.get(id)?.data
        if (a && PersonDisplayProperties.isRelatedAssertionType(a.assertionType)) {
            AsyncData.invalidate(personStore.getAsyncPerson(a.subjectId))
            AsyncData.invalidate(viewPersonStore.getAsyncPerson(a.subjectId))
        }
        // TODO: clear affected cached items
    }

    async function deleteAsync(id: string) {
        const store = useAssertionStore() // use to get/return reactive store objects
        const asyncAssertion = store.asyncAssertions.get(id)
        if (!asyncAssertion)
            throw `Assertion ${id} not found in store`

        await rd.deleteAsync(`assertions/${id}`)

        AsyncData.setDeleted(asyncAssertion)
        invalidateRelated(asyncAssertion)
    }

    function invalidateRelated(asyncAssertion: AsyncData<Assertion>) {
        if (asyncAssertion.data?.subjectType == ItemType.Person) {
            AsyncData.invalidate(personStore.asyncAssertionKeys.get(asyncAssertion.data.subjectId ?? ''))
        }
        else {
            AsyncData.invalidate(familyStore.asyncAssertionKeys.get(asyncAssertion.data?.subjectId ?? ''))
        }

        if (asyncAssertion.data?.assertionType == AssertionType.Child) {
            const childVP = viewPersonStore.asyncViewPersons.get(asyncAssertion.data?.subjectId ?? '')?.data
            AsyncData.invalidate(viewPersonStore.asyncParentFamilyKeys.get(childVP?.id ?? ''))

            const parentVF = viewFamilyStore.asyncViewFamilies.get(asyncAssertion.data?.relatedItemId ?? '')?.data
            AsyncData.invalidate(viewFamilyStore.asyncChildKeys.get(parentVF?.id ?? ''))
        }
    }

    function addAssertionsToStore(plainAssertions: any[], requestTime: DateTime) {
        const store = useAssertionStore() // use to get/return reactive store objects
        const assertionMap = processExpandedAssertions(uniqBy(plainAssertions, 'id'), requestTime)
        AsyncDataStore.addItemMapToStore(store.asyncAssertions, "Assertion", assertionMap, requestTime)
        return assertionMap
    }

    function processExpandedAssertions(plainAssertions: any[], requestTime: DateTime) {
        placeStore.addPlacesToStore(plainAssertions.map(p => p.place).filter(isDefined), requestTime)
        siteStore.addSitesToStore(plainAssertions.map(p => p.site).filter(isDefined), requestTime)
        return AsyncDataStore.createItemMap(plainAssertions, "Assertion", plainToAssertion, a => a.id!)
    }

    return {
        asyncAssertions,

        getAsyncAssertion,
        getAsyncAssertionList,
        areLoadedForPerson,
        getLoadedForPerson,
        getLoadedForFamily,
        areExpandedAssertionsLoaded,
        addAsync,
        patchAsync,
        deleteAsync,
        addAssertionsToStore,
    }
})

function plainToAssertion(p: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    const assertion = assignExisting(new Assertion(), p)
    if (!assertion.subjectType) {
        assertion.subjectType = ItemType.Person
    }
    if (assertion.relatedItemId && !assertion.relatedItemType) {
        assertion.relatedItemType = ItemType.Person
    }
    assertion.date = HistoricalDate.parse(p.date)
    assertion.sortDate = SortDate.parse(p.sortDate)
    assertion.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)
    return assertion
}
